import { PhoneInputFieldData } from '@app/shared/components/phone-input-field/phone-input-field.component';
import { UserDataLabel, UserLabel } from './label.model';
import { UpdateUserCustomField, UserCustomField } from './custom-fields.model';

export interface GeneralResponse {
    message: string;
}

export interface GeneralDataResponse<T> {
    message: string;
    data: T;
}

export interface GeneralDataResponseV2<T> {
    metadata: Metadata;
    data: T;
}

export interface Metadata {
    status: string;
    code: string;
    descriptions: {
        context: string;
        type: string;
        code: string;
        shortDescription: string;
        longDescription: string;
    };
    paginationInfo: string;
}

export interface Organization {
    logo: string;
    name: string;
    snap_enabled: boolean;
}

export interface Profile {
    UserEmail: string;
    address: string;
    firstName: string;
    lastName: string;
    organisationId: number;
    phoneNumber: string;
}

export interface User {
    email: string;
    hasFullAccess: boolean;
    role: string;
}

/* USER LIST */

/**GET USER LIST */
export interface GetOrgUserList {
    message: string;
    data: {
        count: number;
        rows: MemberUser[];
    };
    page: number;
    limit: number;
}
export interface MemberUser {
    id?: number;
    member_ref: string;
    fname: string;
    lname: string;
    gender: string;
    phone_no: string;
    lang: string;
    active: boolean;
    welcome_sent: boolean;
    email: string;
    SeniorCenter?: {
        id: number;
        name: string;
    };
    Programs?: {
        id: number;
        name: string;
    };
    client_delivery_method: {
        checkin_sms: boolean;
        checkin_email: boolean;
        checkin_voice: boolean;
    };
}

/**GET USER */
export interface GetUserResponse {
    message: string;
    data: GetUser;
}
export interface GetUser {
    id?: number;
    member_ref: string;
    client_id?: string;
    fname: string;
    mname: string;
    lname: string;
    age: number;
    gender: string;
    phone_no: string;
    lang: string;
    active: boolean;
    email: string;
    welcome_sent: boolean;
    landline: string;
    consent: any;
    UserInterests: string[];
    client_delivery_method: any;
    zipcode: string;
    race: string;
    birthdate: string;
    disable: boolean | null;
    veteran: boolean | null;
    notes: string;
    user_snap: Snap;
    user_caregiver_interest: UserPH;
    date_activated?: string;
    date_deactive: string;
    date_unsub: string;
    unsubscribed: boolean;
    self_caregiver: SelfCareGiverData;
    cbo_active?: boolean;
    ClientCenters?: OrgUserClientData[];
    medicaidInfo?: MedicaidInfo | null;
    UserLabels: string[];
    customFields?: UserCustomField[];
    caregiver_id?: number | null;
    caregiver_details?: CaregiverDetails | null;
    caregiver_for?: CaregiverForUser[];
    primary_user?: PrimaryUserInfo | null;
    household_members?: HouseholdMemberInfo[];
}

/**ADD USER */
export interface AddUser {
    orgId: number;
    centerId: number;
    fname: string;
    lname: string;
    mname: string;
    age: number;
    lang: string;
    phone_no: string;
    landline: string;
    gender: string;
    email: string;
    obtanined_user_consent: boolean;
    consent_obtained_Date: string;
    explained_privacy_terms_policy: boolean;
    client_delivery_method: any;
    interests: number[];
    notes: string;
    zipcode: string;
    race: string;
    birthdate: string;
    disable: boolean | null;
    veteran: boolean | null;
    user_snap: Snap;
    user_ph: UserPH;
    self_caregiver: SelfCareGiverData;
    client_id: string;
    medicaidInfo?: MedicaidInfo | null;
    customFields?: UpdateUserCustomField[];
    caregiver_id?: number | null;
}
export interface AddUserResponse {
    message: string;
    data: {
        member_ref: string;
        fname: string;
        mname: string;
        lname: string;
        age: number;
        gender: string;
        phone_no: string;
        lang: string;
        active: boolean;
        email: string;
        welcome_sent: boolean;
        landline: string;
        consent: any;
        UserInterests: string[];
        client_delivery_method: any;
        notes: string;
    };
}

/**UPDATE USER */
export interface UpdateUser {
    orgId: number;
    centerId: number;
    fname: string;
    lname: string;
    mname: string;
    age: number;
    active: boolean;
    lang: string;
    phone_no: string;
    gender: string;
    email: string;
    obtanined_user_consent: boolean;
    consent_obtained_Date: string;
    explained_privacy_terms_policy: boolean;
    client_delivery_method: any;
    interests: number[];
    deletedInterests: number[];
    labels: number[]; 
    deletedLabels: number[];
    notes: string;
    zipcode: string;
    race: string;
    birthdate: string;
    disable: boolean | null;
    veteran: boolean | null;
    user_snap: Snap;
    user_ph: UserPH;
    self_caregiver: SelfCareGiverData;
    client_id: string;
}
export interface UpdateUserResponse {
    message: string;
    data: {
        member_ref: string;
        fname: string;
        mname: string;
        lname: string;
        age: number;
        gender: string;
        phone_no: string;
        lang: string;
        active: boolean;
        email: string;
        welcome_sent: boolean;
        landline: string;
        consent: any;
        UserInterests: string[];
        client_delivery_method: any;
        notes: string;
        UserLabels: UserDataLabel[];
    };
}

export interface UserInterestResponse {
    message: string;
    data: UserInterest[];
}
export interface UserInterest {
    id: number;
    name: string;
    is_active?: boolean;
}
export interface UserInterestPost {
    orgId?: number;
    name: string;
}
export interface UserInterestDetail {
    id: number;
    org_id: number;
    interest_id: number;
    name: string;
    usersCount: string;
    remindersCount: string;
    broadcastsCount: string;
    surveysCount: string;
    updated_by: string;
    created_on: string;
    is_active: boolean;
}
export interface UserInterestDetailResponse {
    message: string;
    data: UserInterestDetail[];
    limit: number;
    page: number;
}

export interface BulkInterestStatusPayload {
    interestIds: number[];
    is_active: boolean;
    mode?: 'force' | 'safe_only';
}

export interface BulkInterestStatusResponse {
    archived: number[];
    skipped: number[];
    mode: string;
}

export interface ScheduledInterestInfo {
    id: number;
    name: string;
    scheduledBroadcasts: number;
    scheduledSurveys: number;
    scheduledReminders?: number;
}

export interface ScheduledCommsCheckResponse {
    interestsWithScheduled: ScheduledInterestInfo[];
    scheduledBroadcastIds?: number[];
    scheduledSurveyIds?: number[];
    scheduledReminderIds?: number[];
    scheduledReminderRefs?: string[];
}

export interface InterestDuplicateCheckPayload {
    interest_name: string;
    excludeId?: number;
}

export interface UpdateOrgInterestPayload {
    id: number;
    orgId: number;
    name?: string;
    interest_name?: string;
    is_active?: boolean;
    safeUpdate?: boolean;
}

export interface OrgInterestScheduledCommunications {
    orgInterestIds: number[];
    scheduledBroadcastIds: number[];
    scheduledSurveyIds: number[];
    orgInterests: Array<{ id: number; name: string }>;
}

export interface InterestReminderData {
    program_ref: string;
    name: string;
    active: boolean;
    program?: string;
    sent_date: string;
    id: number;
    deliveryLogs: { dateSent: string };
}

export interface InterestBroadcastData {
    id: number;
    message: string;
    audience: string;
    sent_date: string;
    org_id: number;
}

export interface InterestSurveyData {
    instance_id: string;
    last_run_date: string;
    survey: {
        name: string;
        org_id: number;
        seniorCenter?: { name: string } | null;
    };
    audience?: string;
}
export interface InterestProgramData {
    name: string;
    activeCount: number;
    inactiveCount: number;
    totalCount: number;
}

export interface UserInterestAssociatedWarn {
    id: number;
    name: string;
    eventReminders: InterestReminderData[];
    broadcasts: InterestBroadcastData[];
    surveys: InterestSurveyData[];
    programs: InterestProgramData[];
}

export interface Snap {
    snap_enrollment: boolean | null;
    snap_eligibility: boolean | null;
    snap_interest: boolean | null;
}

export interface UserPH {
    ph_interested: boolean | null;
    ph_consented: boolean | null;
    ph_info_provided: boolean | null;
}

export interface UserResponse {
    message: string;
    data: UserDetail;
}

export interface UserDetail {
    centerId?: number;
    member_ref?: string;
    fname: string;
    mname?: string;
    lname: string;
    age?: number;
    gender: string;
    phone_no?: string;
    lang: string;
    active: boolean;
    email?: string;
    welcome_sent?: boolean;
    landline?: string;
    user_consent: boolean;
    consent_date: string;
    UserInterests: string[];
    client_delivery_method: any;
    notes?: string;
    zipcode: string;
    race: string;
    birthdate: string;
    disable: boolean | null;
    veteran: boolean | null;
    user_snap: Snap;
    user_ph: UserPH;
    self_caregiver: SelfCareGiverData;
}

export interface UserFormData {
    id?: number;
    active: boolean;
    centerId?: number;
    member_ref: string;
    client_id?: string;
    fname: string;
    lname: string;
    mname: string;
    age: any;
    gender: string;
    phone_no: PhoneInputFieldData;
    landline: PhoneInputFieldData;
    email: string;
    lang: string;
    interests: string[];
    delivery_method: string[];
    user_consent?: boolean;
    obtained_date?: Date | null;
    terms_policy?: boolean;
    welcome_sent: boolean;
    notes: string;
    snap_enrollment?: boolean | 0;
    snap_eligibility?: boolean | 0;
    snap_interest?: boolean | 0;
    ph_interested?: boolean | 0;
    ph_consented?: boolean | 0;
    ph_info_provided?: boolean | 0;
    date_activated?: string;
    date_deactive?: string;
    date_unsub?: string;
    unsubscribed?: boolean;
    zipcode: string;
    race: string;
    birthdate: string;
    disable: boolean | 0;
    veteran: boolean | 0;
    self_caregiver: SelfCareGiverFormData;
    cbo_active?: boolean;
    ClientCenters?: OrgUserClientData[];
    programIds?: number[];
    mediCaidId?: string;
    mcoEnrollmentDate?: string;
    mcoDisEnrollmentDate?: string;
    UserLabels: string[];
    customFields?: UserCustomField[];
    caregiver_id?: number | null;
    caregiver_details?: CaregiverDetails | null;
    caregiver_for?: CaregiverForUser[];
    primary_user?: PrimaryUserInfo | null;
    household_members?: HouseholdMemberInfo[];
}

interface DeliveryMethod {
    checkin_sms: boolean;
    checkin_email: boolean;
    checkin_voice: boolean;
}

interface UserConsent {
    obtanined_user_consent: boolean;
    consent_obtained_Date: string;
    explained_privacy_terms_policy: boolean;
}

export interface BulkUploadBody {
    file?: File;
    isExistingUserScan?: boolean;
    validateUsers?: boolean;
    users?: CsvUserJSON[];
    source?: string;
}
export interface BulkUploadResponse {
    message?: string;
    uploadRecordId?: string;
    data: BulkUploadResponseUser[];
    error?: string;
}
export class CsvUserError {
    value: string = '';
    column: string = '';
    row: number = 0;
    message: string = '';
    label: string = '';
}

/**class form User details of bulk*/
export class CsvUserJSON {
    orgId: number = 0;
    programIds: number[] = [];
    fname: string = '';
    lname: string = '';
    mname: string = '';
    age: number = 0;
    lang: string = '';
    phone_no: string = '';
    landline: string = '';
    gender: string = '';
    email: string = '';
    interests: number[] = [];
    zipcode: string = '';
    race: string = '';
    birthdate: string = '';
    disable: boolean | null = null;
    veteran: boolean | null = null;
    notes: string = '';
    updateLanguage: boolean = false;
    snap: {
        snap_enrollment?: boolean | null;
        snap_eligibility?: boolean | null;
        snap_interest?: boolean | null;
    };
    consent: {
        obtanined_user_consent: boolean;
        consent_obtained_Date: string | null;
        explained_privacy_terms_policy: boolean;
    };
    client_delivery_method: {
        checkin_sms: boolean;
        checkin_email: boolean;
        checkin_voice: boolean;
    };
    client_id: string = '';
    caregiver_name: string = '';
    caregiver_email: string = '';
    caregiver_phone: string = '';

    constructor() {
        this.client_delivery_method = {
            checkin_sms: false,
            checkin_voice: false,
            checkin_email: false
        };
        this.consent = {
            obtanined_user_consent: false,
            consent_obtained_Date: null,
            explained_privacy_terms_policy: false
        };
        this.snap = {
            snap_enrollment: null,
            snap_eligibility: null,
            snap_interest: null
        };
    }
}

export class UserDemographicFields {
    zipcode: string = '';
    race: string = '';
    birthdate: string = '';
    disable: boolean | null = null;
    veteran: boolean | null = null;
}

export type SelfCareGiverData = 'Yes' | 'No' | 'Unsure' | null;
export type SelfCareGiverFormData = 'Yes' | 'No' | 'Unsure' | 0;

export interface SelectorUserData {
    member_ref: string;
    fname: string;
    lname: string;
    Programs?: {
        name: string;
    };
    phone_no?: string;
    lang?: string;
}
export interface DownloadUsersPayload {
    unsubscribed?: string;
    active?: string;
    gender?: string;
    language?: string;
    interest_ids?: string[];
    zipcode?: string[];
    center?: number;
    program?: number[];
    searchText?: string;
    searchTerm?: string;
    household_role?: string;
    label_ids?: string[];
}

/** SINGLE USER ARCHITECTURE API MODELS */
export interface OrgUserData {
    centerIds: number[];
    fname: string;
    lname: string;
    mname: string;
    gender: string;
    phone_no: string;
    landline: string;
    email: string;
    lang: string;
    interests: number[];
    client_id: string;
    zipcode: string;
    race: string;
    birthdate: string;
    disable: boolean;
    veteran: boolean;
    notes: string;
    self_caregiver: string;
    client_delivery_method: {
        checkin_email: boolean;
        checkin_sms: boolean;
        checkin_voice: boolean;
    };
    orgId: number;
    age: number;
    snap: {
        snap_enrollment?: boolean | null;
        snap_eligibility?: boolean | null;
        snap_interest?: boolean | null;
    };
    user_ph: {
        ph_interested?: boolean | null;
        ph_consented?: boolean | null;
        ph_info_provided?: boolean | null;
    };
}

export interface OrgUserClientData {
    client_id: number;
    SeniorCenter: {
        id: number;
        name: string;
    };
}

export interface BulkUploadDuplicateResponseUser {
    row: number;
    type: string;
    orgId: number;
    programIds: number[];
    fname: string;
    lname: string;
    mname: string;
    age: number;
    lang: string;
    phone_no: string;
    landline: string;
    gender: string;
    email: string;
    interests: number[];
    zipcode: string;
    race: string;
    birthdate: string;
    disable: boolean;
    veteran: boolean;
    notes: string;
    client_id: string;
    client_delivery_method: {
        checkin_sms: boolean;
        checkin_voice: boolean;
        checkin_email: boolean;
    };
    consent: {
        obtanined_user_consent: boolean;
        consent_obtained_Date: string;
        explained_privacy_terms_policy: boolean;
    };
    snap: {
        snap_enrollment: boolean;
        snap_eligibility: boolean;
        snap_interest: boolean;
    };
}
export interface BulkUploadResponseUser {
    id: number;
    member_ref: string;
    fname: string;
    lname: string;
    age: number;
    gender: string;
    phone_no: string;
    email: string;
    lang: string;
    senior_center_id: number;
    active: boolean;
    welcome_sent: boolean;
    deleted: boolean;
    updated_by: string;
    created_on: string;
    updated_on: string;
    mname: string;
    date_activated: string;
    landline: string;
    user_consent: string;
    consent_date: string;
    notes: string;
    unsubscribed: string;
    birth_date: string;
    date_unsub: string;
    date_deactive: string;
    portal_updated_by: string;
    zipcode: string;
    birthdate: string;
    race: string;
    disable: boolean;
    veteran: boolean;
    self_caregiver: string;
    client_id: string;
    member_ref_old: string;
    cbo_active: boolean;
    org_id: number;
    household_id: number;
    duplicates: BulkUploadDuplicateResponseUser[];
    delivery_method: {
        checkin_sms: boolean;
        checkin_voice: boolean;
        checkin_email: boolean;
    };
}

export interface MedicaidInfo {
    mediCaidId: string;
    mcoEnrollmentDate: string;
    mcoDisEnrollmentDate: string;
}

export interface CaregiverDetails {
    member_ref?: string;
    fname: string;
    lname: string;
    phone_no: string;
    email: string;
    status?: string;
}

export interface CaregiverForUser {
    name: string;
    member_ref: string;
    phone_no: string | null;
    email: string;
}

export interface PrimaryUserInfo {
    id: number;
    member_ref: string;
    fname: string;
    lname: string;
    phone_no: string;
    email: string;
}

export interface HouseholdMemberInfo {
    member_ref: string;
    fname: string;
    lname: string;
    unsubscribed: boolean;
}
